import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Download, Loader2, QrCode, ShieldCheck, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const INDIAN_STATES = ["Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Odisha", "Punjab", "Rajasthan", "Tamil Nadu", "Telangana", "Uttar Pradesh", "Uttarakhand", "West Bengal"];

const emptyForm = { name: "", phone: "", email: "", address: "", city: "", state: "Karnataka", pincode: "", payment_method: "upi", upi_id: "" };

function FakeQR() {
  const cells = Array.from({ length: 49 }, (_, i) => (i * 7 + 3) % 3 !== 0);
  return (
    <div className="bg-white rounded-2xl border border-ink/10 p-4 flex flex-col items-center gap-2" data-testid="checkout-upi-qr">
      <div className="grid grid-cols-7 gap-0.5 w-28 h-28">
        {cells.map((on, i) => (
          <div key={i} className={on ? "bg-ink" : "bg-white"} />
        ))}
      </div>
      <p className="font-mono text-[9px] tracking-widest uppercase text-charcoal/60 text-center">
        Scan with GPay / PhonePe / Paytm<br />(simulation — no real charge)
      </p>
    </div>
  );
}

export default function CheckoutModal() {
  const { checkoutOpen, setCheckoutOpen, cart, subtotal, clearCart, customer, setLoginOpen } = useStore();
  const [form, setForm] = useState(emptyForm);
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);
  const [phase, setPhase] = useState(null); // null | "processing" | "failed"
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [saveAddr, setSaveAddr] = useState(true);
  const [payConfig, setPayConfig] = useState(null);

  useEffect(() => {
    if (checkoutOpen && customer) {
      setForm((f) => ({
        ...f,
        name: customer.name || f.name,
        phone: customer.phone || f.phone,
        email: customer.email || f.email,
      }));
      api.get("/my/addresses").then((r) => setAddresses(r.data.addresses || [])).catch(() => {});
    }
    if (checkoutOpen && !payConfig) {
      api.get("/payments/config").then((r) => setPayConfig(r.data)).catch(() => setPayConfig({ razorpay: false }));
    }
  }, [checkoutOpen, customer]);

  const applyAddress = (a) =>
    setForm((f) => ({ ...f, address: a.address, city: a.city, state: a.state, pincode: a.pincode }));

  const deleteAddress = async (id) => {
    try {
      const { data } = await api.delete(`/my/addresses/${id}`);
      setAddresses(data.addresses || []);
    } catch { /* keep list as-is */ }
  };

  const pay = async (orderId) => {
    setPendingOrderId(orderId);
    setPhase("processing");
    await new Promise((r) => setTimeout(r, 2200));
    try {
      const { data } = await api.post(`/orders/${orderId}/payment-result`, { outcome: "success" });
      if (saveAddr && customer && form.address.trim() && !addresses.some((a) => a.address === form.address.trim())) {
        api.post("/my/addresses", {
          label: addresses.length ? `Address ${addresses.length + 1}` : "Home",
          address: form.address, city: form.city, state: form.state, pincode: form.pincode,
        }).catch(() => {});
      }
      setPhase(null);
      setOrder(data);
      clearCart();
    } catch (e) {
      setPhase(null);
      toast.error(formatApiError(e));
    }
  };

  const failPayment = async (orderId) => {
    await api.post(`/orders/${orderId}/payment-result`, { outcome: "failed" }).catch(() => {});
    setPhase("failed");
  };

  const saveAddressMaybe = () => {
    if (saveAddr && customer && form.address.trim() && !addresses.some((a) => a.address === form.address.trim())) {
      api.post("/my/addresses", {
        label: addresses.length ? `Address ${addresses.length + 1}` : "Home",
        address: form.address, city: form.city, state: form.state, pincode: form.pincode,
      }).catch(() => {});
    }
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const razorpayFlow = async (orderId) => {
    setPendingOrderId(orderId);
    setPhase("processing");
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load the payment gateway — check your connection");
      const { data: rzp } = await api.post("/payments/razorpay/order", { order_id: orderId });
      const r = new window.Razorpay({
        key: rzp.key_id,
        amount: rzp.amount,
        currency: rzp.currency,
        order_id: rzp.razorpay_order_id,
        name: "Baeboo's Wardrobe",
        description: `Order ${orderId}`,
        prefill: { name: form.name, contact: `+91${form.phone}`, email: form.email || undefined },
        theme: { color: "#D97757" },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/payments/razorpay/verify", { order_id: orderId, ...resp });
            saveAddressMaybe();
            setPhase(null);
            setOrder(data);
            clearCart();
          } catch (e) {
            setPhase("failed");
            toast.error(formatApiError(e));
          }
        },
        modal: { ondismiss: () => setPhase("failed") },
      });
      r.on("payment.failed", async () => {
        await api.post(`/orders/${orderId}/payment-result`, { outcome: "failed" }).catch(() => {});
        setPhase("failed");
      });
      setPhase(null);
      r.open();
    } catch (e) {
      setPhase(null);
      toast.error(formatApiError(e));
    }
  };

  const shipping = subtotal >= 1999 ? 0 : 99;
  const close = () => { setCheckoutOpen(false); setPhase(null); setPendingOrderId(null); if (order) { setOrder(null); setForm(emptyForm); } };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async () => {
    if (!form.name.trim() || !form.address.trim() || !form.city.trim()) {
      toast.error("Please fill name, address and city");
      return;
    }
    setPlacing(true);
    try {
      const payload = {
        ...form,
        items: cart.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })),
      };
      const { data } = await api.post("/orders", payload);
      if (payConfig?.razorpay) razorpayFlow(data.id);
      else pay(data.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPlacing(false);
    }
  };

  const downloadReceipt = () => {
    if (!order) return;
    const lines = [
      "BAEBOO'S WARDROBE — ORDER RECEIPT",
      `Order ID: ${order.id}`,
      `Date: ${new Date(order.created_at).toLocaleString("en-IN")}`,
      "----------------------------------------",
      ...order.items.map((i) => `${i.name} | ${i.size} | x${i.qty} | ${inr(i.price * i.qty)}`),
      "----------------------------------------",
      `Subtotal: ${inr(order.subtotal)}`,
      `Shipping: ${order.shipping === 0 ? "FREE" : inr(order.shipping)}`,
      `Total: ${inr(order.total)}`,
      "Payment: UPI",
      `Ship to: ${order.name}, ${order.address}, ${order.city}, ${order.state} - ${order.pincode}`,
      "Thank you for shopping with Baeboo's Wardrobe!",
    ].join("\n");
    const url = URL.createObjectURL(new Blob([lines], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${order.id}-receipt.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const inputCls = "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3 text-sm outline-none focus:border-terracotta transition-colors";

  return (
    <AnimatePresence>
      {checkoutOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
          onClick={close}
          data-testid="checkout-modal"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-cream w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-[2rem] sm:rounded-[2rem] p-6 sm:p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif font-black text-2xl sm:text-3xl text-ink">{order ? "Order confirmed!" : "Guest Checkout"}</h3>
              <button onClick={close} className="rounded-full p-2 hover:bg-blush transition-colors" data-testid="checkout-close" aria-label="Close checkout">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!customer ? (
              <div className="text-center py-14" data-testid="checkout-login-gate">
                <ShieldCheck className="w-12 h-12 text-terracotta mx-auto mb-5" />
                <p className="font-serif font-bold text-2xl text-ink">Login before payment</p>
                <p className="text-sm text-charcoal mt-2 max-w-xs mx-auto">
                  A quick mobile OTP keeps every order linked to your number and trackable. Your basket is saved.
                </p>
                <button
                  onClick={() => { setCheckoutOpen(false); setLoginOpen(true); }}
                  className="mt-6 rounded-full bg-terracotta text-white px-8 py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors"
                  data-testid="checkout-gate-login-btn"
                >
                  Login with OTP
                </button>
              </div>
            ) : phase === "processing" ? (
              <div className="text-center py-16" data-testid="checkout-processing">
                <Loader2 className="w-12 h-12 animate-spin text-terracotta mx-auto mb-5" />
                <p className="font-serif font-bold text-2xl text-ink">Processing UPI payment...</p>
                <p className="text-sm text-charcoal mt-2 max-w-xs mx-auto">{payConfig?.razorpay ? "Opening the secure Razorpay window — UPI, cards, netbanking." : "Approve the collect request in your UPI app. The order confirms only after payment succeeds (simulated)."}</p>
                {!payConfig?.razorpay && (
                  <button onClick={() => failPayment(pendingOrderId)} className="mt-6 font-mono text-[10px] tracking-widest uppercase text-charcoal/50 hover:text-red-500 transition-colors" data-testid="checkout-cancel-payment">
                    Cancel payment
                  </button>
                )}
              </div>
            ) : phase === "failed" ? (
              <div className="text-center py-16" data-testid="checkout-payment-failed">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-5" />
                <p className="font-serif font-bold text-2xl text-ink">Payment failed</p>
                <p className="text-sm text-charcoal mt-2">Your order is not confirmed and no money was charged.</p>
                <div className="flex gap-3 justify-center mt-6">
                  <button onClick={() => (payConfig?.razorpay ? razorpayFlow(pendingOrderId) : pay(pendingOrderId))} className="rounded-full bg-terracotta text-white px-8 py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors" data-testid="checkout-retry-payment">
                    Retry Payment
                  </button>
                  <button onClick={close} className="rounded-full border-2 border-ink/15 px-8 py-3.5 text-sm font-semibold" data-testid="checkout-failed-close">
                    Close
                  </button>
                </div>
              </div>
            ) : order ? (
              <div data-testid="checkout-success">
                <div className="bg-mint rounded-2xl p-6 text-center">
                  <CheckCircle2 className="w-12 h-12 text-sage-dark mx-auto mb-3" />
                  <p className="font-mono text-xs tracking-[0.3em] uppercase text-sage-dark">Order ID</p>
                  <p className="font-mono font-bold text-2xl text-ink mt-1" data-testid="checkout-order-id">{order.id}</p>
                  <p className="text-sm text-charcoal mt-3">
                    UPI payment of {inr(order.total)} <strong className="text-sage-dark">successful</strong> (simulation — no real charge).{" "}
                    Order confirmed! We'll WhatsApp updates to {order.phone}.
                  </p>
                </div>
                <div className="mt-5 space-y-2">
                  {order.items.map((i) => (
                    <div key={i.product_id + i.size} className="flex justify-between text-sm bg-white rounded-xl px-4 py-3">
                      <span>{i.name} · {i.size} · x{i.qty}</span>
                      <span className="font-semibold">{inr(i.price * i.qty)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-serif font-bold text-lg px-4 pt-2">
                    <span>Total</span>
                    <span className="text-terracotta" data-testid="checkout-order-total">{inr(order.total)}</span>
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <button onClick={downloadReceipt} className="flex-1 rounded-full border-2 border-ink py-3 text-sm font-semibold hover:bg-ink hover:text-cream transition-colors flex items-center justify-center gap-2" data-testid="checkout-download-receipt">
                    <Download className="w-4 h-4" /> Receipt
                  </button>
                  <button onClick={close} className="flex-1 rounded-full bg-terracotta text-white py-3 text-sm font-semibold hover:bg-terracotta-dark transition-colors" data-testid="checkout-continue-shopping">
                    Continue Shopping
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="checkout-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="checkout-guest-name-input" />
                  <div className="relative">
                    <input className={inputCls} placeholder="Mobile (10 digits) *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} readOnly={Boolean(customer?.phone)} title={customer?.phone ? "Linked to your login number" : ""} data-testid="checkout-guest-phone-input" />
                    {customer?.phone && <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[9px] tracking-widest uppercase text-sage-dark">Logged in</span>}
                  </div>
                </div>
                <input className={inputCls} type="email" placeholder="Email (for order updates)" value={form.email} onChange={set("email")} data-testid="checkout-guest-email-input" />
                {customer && addresses.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="checkout-saved-addresses">
                    {addresses.map((a) => (
                      <span key={a.id} className="flex items-center gap-1.5 rounded-full border border-ink/15 bg-white pl-4 pr-2 py-1.5 text-xs">
                        <button onClick={() => applyAddress(a)} className="font-semibold hover:text-terracotta transition-colors" data-testid={`checkout-address-${a.id}`}>
                          {a.label} · {a.city}
                        </button>
                        <button onClick={() => deleteAddress(a.id)} className="text-charcoal/40 hover:text-red-500 transition-colors" data-testid={`checkout-address-delete-${a.id}`} aria-label={`Delete ${a.label}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <input className={inputCls} placeholder="Address — house, street, landmark *" value={form.address} onChange={set("address")} data-testid="checkout-guest-address-input" />
                {customer && (
                  <label className="flex items-center gap-2 text-xs text-charcoal">
                    <input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} data-testid="checkout-save-address" />
                    Save this address for faster checkout next time
                  </label>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <input className={inputCls} placeholder="City *" value={form.city} onChange={set("city")} data-testid="checkout-guest-city-input" />
                  <select className={inputCls} value={form.state} onChange={set("state")} data-testid="checkout-guest-state-select">
                    {INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <input className={inputCls} placeholder="Pincode *" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} data-testid="checkout-guest-pincode-input" />
                </div>

                <div className="rounded-2xl border-2 border-terracotta bg-peach p-4 pt-2 mt-2" data-testid="checkout-pay-upi-info">
                  <QrCode className="w-5 h-5 text-terracotta mb-2" />
                  <p className="font-semibold text-sm">UPI — GPay / PhonePe / Paytm</p>
                  <p className="text-xs text-charcoal mt-1">{payConfig?.razorpay ? "Secure Razorpay checkout — UPI, cards & more. Real payment, instant confirmation." : "Pay securely with any UPI app. Currently simulated — no real charge."}</p>
                </div>
                {!payConfig?.razorpay && (
                  <div className="grid sm:grid-cols-2 gap-4 items-center">
                    <FakeQR />
                    <input className={inputCls} placeholder="Your UPI ID (e.g. name@okhdfc)" value={form.upi_id} onChange={set("upi_id")} data-testid="checkout-upi-id-input" />
                  </div>
                )}

                <div className="bg-white rounded-2xl p-4 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-charcoal">Subtotal ({cart.reduce((s, i) => s + i.qty, 0)} items)</span><span>{inr(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-charcoal">Shipping</span><span>{shipping === 0 ? "FREE" : inr(shipping)}</span></div>
                  <div className="flex justify-between font-serif font-bold text-lg pt-1 border-t border-ink/10">
                    <span>Total</span><span className="text-terracotta" data-testid="checkout-total">{inr(subtotal + shipping)}</span>
                  </div>
                </div>

                <button
                  onClick={placeOrder}
                  disabled={placing || cart.length === 0}
                  className="w-full rounded-full bg-terracotta text-white py-4 text-sm font-semibold hover:bg-terracotta-dark transition-colors disabled:opacity-50"
                  data-testid="checkout-place-order-btn"
                >
                  {placing ? "Placing your order..." : `Pay ${inr(subtotal + shipping)} via UPI`}
                </button>
                <p className="text-center font-mono text-[10px] text-charcoal/60">Free shipping above ₹1,999 · No account needed</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
